import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileCallBar from "../components/MobileCallBar";

export default function PrivacyPolicy() {
    return (
        <div className="bg-white font-sans text-navy">
            <Seo
                title="Privacy Policy | Hexagon EOR"
                description="How Hexagon EOR collects, uses, and protects personal information across its website and communications."
                path="/privacy-policy"
            />
            <Header />
            <main className="mx-auto max-w-3xl px-5 pb-24 pt-36 sm:px-8 lg:pt-40">
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                    <span className="h-px w-10 bg-gold" aria-hidden="true" />
                    Legal
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tighter text-navy sm:text-5xl">
                    Privacy Policy
                </h1>
                <p className="mt-4 text-sm text-navy/50">
                    Last updated: July 2026
                </p>

                <div className="prose-legal mt-10" data-testid="privacy-policy-content">
                    <h2>1. Scope and Purpose of This Policy</h2>
                    <p>
                        This Privacy Policy explains how Hexagon EOR
                        ("Hexagon", "we", "us", or "our") handles personal
                        information in connection with our website at
                        hexagoneor.com and our telephone and email
                        communications. It applies to visitors of our website
                        and to individuals who contact us about our employer of
                        record and employment administration services.
                    </p>

                    <h2>2. Information You Voluntarily Provide</h2>
                    <p>
                        Our website does not contain contact forms, inquiry
                        forms, or account registration. The only way you share
                        personal information with us is by contacting us
                        directly, by phone or by email. Depending on your
                        communication, this may include:
                    </p>
                    <ul>
                        <li>Your name and the business you represent</li>
                        <li>Your email address and telephone number</li>
                        <li>Details about your hiring goals, markets of interest, and workforce needs</li>
                        <li>Any other information you choose to share during a call or in an email</li>
                    </ul>

                    <h2>3. Information Collected Automatically</h2>
                    <p>
                        When you visit our website, certain technical
                        information may be collected automatically by our
                        hosting infrastructure and by the website analytics
                        tools we use to understand aggregate usage. This may
                        include your IP address, browser type and version,
                        device type, pages visited, time spent on pages,
                        referring URLs, and approximate location derived from
                        your IP address. This information is used in aggregate
                        to operate, secure, and improve the website.
                    </p>

                    <h2>4. Cookies and Similar Technologies</h2>
                    <p>
                        Our website may use a limited number of cookies and
                        similar technologies that are necessary for the site to
                        function and that support aggregated usage analytics.
                        We do not use advertising cookies or cross-site
                        tracking technologies, and we do not build advertising
                        profiles about visitors. Most web browsers allow you to
                        control or delete cookies through their settings;
                        disabling cookies may affect how parts of the website
                        function.
                    </p>

                    <h2>5. How We Use Personal Information</h2>
                    <p>We use personal information to:</p>
                    <ul>
                        <li>Respond to your calls and emails and discuss our services with you</li>
                        <li>Prepare and administer service agreements where you choose to proceed</li>
                        <li>Operate, maintain, secure, and improve our website</li>
                        <li>Understand aggregate website usage so we can improve content and usability</li>
                        <li>Comply with legal obligations and protect our legal rights</li>
                    </ul>
                    <p>
                        We do not sell your personal information, and we do not
                        share it with third parties for their own marketing
                        purposes.
                    </p>

                    <h2>6. Service Providers and Information Sharing</h2>
                    <p>
                        We may share personal information with trusted service
                        providers who support our operations — such as website
                        hosting providers, analytics providers, email and
                        telephony services, and professional advisers — but
                        only to the extent needed for them to perform services
                        for us and subject to appropriate confidentiality
                        obligations. We may also disclose information where
                        required by law, regulation, or legal process, or in
                        connection with a business transfer such as a merger or
                        acquisition, in which case the information remains
                        subject to the commitments in this policy.
                    </p>

                    <h2>7. Data Retention</h2>
                    <p>
                        We retain personal information only for as long as
                        reasonably necessary for the purposes described in this
                        policy, including maintaining business records,
                        resolving disputes, and meeting legal or regulatory
                        requirements. When information is no longer needed, we
                        delete it or de-identify it.
                    </p>

                    <h2>8. Security Practices</h2>
                    <p>
                        We use administrative, technical, and organizational
                        measures designed to protect personal information
                        against unauthorized access, loss, or misuse. No method
                        of transmission or storage is completely secure,
                        however, and we cannot guarantee absolute security.
                    </p>

                    <h2>9. Your Privacy Rights</h2>
                    <p>
                        Depending on where you live, you may have rights
                        regarding your personal information, including the
                        right to request access to it, correct inaccuracies, or
                        request its deletion, subject to legal exceptions.
                    </p>
                    <ul>
                        <li>
                            United States residents, including California
                            residents under the CCPA/CPRA and residents of
                            other states with privacy laws, may have rights to
                            know, access, correct, delete, and to
                            non-discrimination for exercising those rights.
                        </li>
                        <li>
                            Individuals in Canada may have rights under PIPEDA
                            and applicable provincial privacy laws, including
                            access to and correction of their personal
                            information.
                        </li>
                    </ul>
                    <p>
                        To exercise any of these rights, contact us using the
                        details in the Contact section below. We may need to
                        verify your identity before completing a request.
                    </p>

                    <h2>10. Cross-Border Data Handling</h2>
                    <p>
                        We primarily serve businesses in the United States and
                        Canada. Personal information may be processed in either
                        country, or by service providers located elsewhere, and
                        may therefore be subject to the laws of those
                        jurisdictions. We take steps intended to ensure such
                        information continues to receive appropriate
                        protection.
                    </p>

                    <h2>11. Children's Privacy</h2>
                    <p>
                        Our website and services are intended for businesses
                        and their representatives. They are not directed to
                        children, and we do not knowingly collect personal
                        information from anyone under the age of 16. If you
                        believe a child has provided us personal information,
                        please contact us so we can delete it.
                    </p>

                    <h2>12. Changes to This Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to
                        reflect changes in our practices or legal requirements.
                        The updated version will be posted on this page with a
                        revised "Last updated" date. We encourage you to review
                        this page periodically.
                    </p>

                    <h2>13. Contact Us About Privacy</h2>
                    <p>
                        If you have questions about this Privacy Policy, wish
                        to exercise your privacy rights, or have a privacy
                        concern, you can reach us at{" "}
                        <a href="mailto:info@hexagoneor.com" data-testid="privacy-email-link">
                            info@hexagoneor.com
                        </a>{" "}
                        or by phone at{" "}
                        <a href="tel:+18333439367" data-testid="privacy-phone-link">
                            +1 833-343-9367
                        </a>
                        .
                    </p>
                </div>
            </main>
            <Footer />
            <div className="h-[68px] md:hidden" aria-hidden="true" />
            <MobileCallBar />
        </div>
    );
}
